import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import snakeCase from 'lodash/snakeCase';
import map from 'lodash/map';
import max from 'lodash/max';
import first from 'lodash/first';
import last from 'lodash/last';
import every from 'lodash/every';
import clone from 'lodash/clone';
import find from 'lodash/find';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import { IServerDispatcher, IServerDriver, IServerLogVehicle, Log } from '../dashboard/logs/log';

import { IMoment, moment } from '@lib/moment';
import { generateUuid, kmToMiles, METERS_PER_KM } from '@lib/utils';
import { totalMilesFromOdometers } from './logs';

import { ILogEvent, ILogRemark } from '@app/dashboard/logs/log-view-model';
import { IHourlyBreadcrumb } from '@app/dashboard/trips/bulk/bulk-common-service';
import { IDrivingPeriod } from '@lib/data/driving-period-view-model';

export interface ILogSuggestionBodyObject {
  [key: string]: ILogSuggestionBody;
}

export interface ILogSuggestionData {
  body: ILogSuggestionBodyObject;
  log_ids: number[];
  driver_ids: number[];
  driving_period_ids: number[];
  reason: string;
  approveable: boolean;
  id?: number;
  suggested_at?: string;
  drivers?: IServerDriver[];
  approved_by?: IServerDriver[];
  dispatchers?: IServerDispatcher[];
}

export interface ILogSuggestion {
  id: number;
  body: {
    [key: string]: ILogSuggestionBody;
  };
  logIds: number[];
  driverIds: number[];
  drivingPeriodIds: number[];
  suggestedAt: IMoment;
  drivers: {
    name: string;
    firstName: string;
    lastName: string;
    approved?: boolean;
  };
  dispatcherNames: string[];
  changedAttributes: string[];
  changedEvents: string[];
  changedRemarks: string[];
  reason: string;
}


interface ILogSuggestionBody {
  logs?: ILogSuggestionLogData[];
  events?: ILogSuggestionEventData[];
  remarks?: ILogSuggestionRemarkData[];
}

export interface ILogSuggestionLogData {
  offline_id?: string;
  total_miles?: number[];
  terminal_street?: string[];
  terminal_city?: string[];
  notes?: string[];
  vehicle_ids?: number[];
  asset_ids?: number[];
  tractor_nums?: number[];
  trailer_nums?: number[];
  odometers?: number[];
  ref_no?: number[];
  carrier_name?: string[];
  carrier_street?: string[];
  carrier_city?: string[];
  carrier_state?: string[];
  carrier_zip?: string[];
  terminal_state?: string[];
  terminal_zip?: string[];
  co_driver_ids?: number[];
  co_driver_full_names?: string[];
  origin?: string[];
  destination?: string[];
  short_haul?: boolean[];
  total_pc_meters?: number[];
  adverse_driving?: boolean[];
  exception_ag_driving?: boolean[];
}

export interface ILogSuggestionEventData {
  offline_id?: string;
  notes?: string[];
  time?: string[];
  type?: string[];
  location?: string[];
  is_manual?: boolean[];
  driving_period_id?: number;
  source_driving_period_id?: number;
  unassign_driving_period_id?: number;
  eld_event_offline_id?: string[];
}

export interface ILogSuggestionRemarkData {
  offline_id?: string;
  notes?: string[];
  time?: string[];
  location?: string[];
  exception_log_offline_id?: string[];
}

export interface IOdometerEntry {
  start: number;
  end: number;
  metric_units: boolean;
}

export interface IReassignTripOptions {
  setTotalPCMeters?: boolean;
  isBulkOperation?: boolean;
}

export const DRIVING_AG_EXCEPTION = 'exception_ag_driving';
export const EVENT_ATTRIBUTES = ['type', 'time', 'location', 'notes', 'is_manual'];
export const REMARK_ATTRIBUTES = ['time', 'location', 'notes', 'exception_log_offline_id'];
export const LOG_ATTRIBUTES = [
  'vehicle_ids',
  'asset_ids',
  'tractor_nums',
  'trailer_nums',
  'total_miles',
  'odometers',
  'ref_no',
  'carrier_name',
  'carrier_street',
  'carrier_city',
  'carrier_state',
  'carrier_zip',
  'terminal_street',
  'terminal_city',
  'terminal_state',
  'terminal_zip',
  'co_driver_ids',
  'co_driver_full_names',
  'origin',
  'destination',
  'notes',
  'short_haul',
  'total_pc_meters',
  'adverse_driving',
  'exception_ag_driving',
];

function toDateFormat(datetime: IMoment): string {
  return datetime.formatToServerUtc();
}

function isBlank(value: string | any[] | object): boolean {
  return isNil(value) || value === '';
}

function normaliseAttr(value) {
  if (moment.isMoment(value)) {
    return toDateFormat(value);
  } else if (isNil(value)) {
    // return null if value is null or undefined
    return null;
  } else {
    return value;
  }
}

function isAttrEqual(oldValue, newValue) {
  oldValue = isBlank(oldValue) ? null : oldValue;
  newValue = isBlank(newValue) ? null : newValue;
  if (isArray(oldValue) && isArray(newValue)) {
    return isEqual(oldValue.sort(), newValue.sort());
  } else if (isObject(oldValue) && isObject(newValue)) {
    return isEqual(oldValue, newValue);
  } else {
    return oldValue === newValue;
  }
}

export class LogSuggestion {
  public data: ILogSuggestionData;

  constructor(data = null) {
    if (data) {
      this.data = data;
    } else {
      this.clear();
    }
  }

  get id(): number {
    return this.data.id;
  }

  get reason(): string {
    return this.data.reason;
  }

  set reason(reason: string) {
    this.data.reason = reason;
  }

  get approveable(): boolean {
    return this.data.approveable;
  }

  set approveable(approveable: boolean) {
    this.data.approveable = approveable;
  }

  public clear(): void {
    this.data = {
      body: {},
      log_ids: [],
      driver_ids: [],
      driving_period_ids: [],
      reason: null,
      approveable: false,
    };
  }

  public getDriver(driverId: number | string, create = false): ILogSuggestionBody {
    const data = this.data;
    driverId = Number(driverId);
    if (!data.body[driverId] && create) {
      data.body[driverId] = {};
      data.driver_ids.push(driverId);
    }
    return data.body[driverId] || {};
  }

  public getLog(driverId: number, offlineId: string, create = false): ILogSuggestionLogData {
    const driver: ILogSuggestionBody = this.getDriver(driverId, true);
    if (!driver.logs) driver.logs = [];

    let log = driver.logs.find(l => l.offline_id === offlineId);
    if (!log && create) {
      log = { offline_id: offlineId };
      driver.logs.push(log);
    }
    return log;
  }

  public getEvent(driverId: number, offlineId: string, create = false): ILogSuggestionEventData {
    const driver: ILogSuggestionBody = this.getDriver(driverId, true);
    if (!driver.events) driver.events = [];

    let event = driver.events.find(e => e.offline_id === offlineId);
    if (!event && create) {
      event = !!offlineId ? { offline_id: offlineId } : {};
      driver.events.push(event);
    }
    return event;
  }

  public getRemark(driverId: number, offlineId: string, create = false): ILogSuggestionRemarkData {
    const driver: ILogSuggestionBody = this.getDriver(driverId, true);
    if (!driver.remarks) driver.remarks = [];

    let remark = driver.remarks.find(r => r.offline_id === offlineId);
    if (!remark && create) {
      remark = { offline_id: offlineId };
      driver.remarks.push(remark);
    }
    return remark;
  }

  public getRemarkChangesForDriver(driverId: number): ILogSuggestionRemarkData[] {
    const driver: ILogSuggestionBody = this.getDriver(driverId);
    return driver.remarks || [];
  }

  public getEventChangesForDriver(driverId: number): ILogSuggestionEventData[] {
    const driver: ILogSuggestionBody = this.getDriver(driverId);
    return driver.events || [];
  }

  public clearAttributesForLog(driverId: number, offlineId: string): void {
    const driver: ILogSuggestionBody = this.getDriver(driverId);
    if (isEmpty(driver.logs)) return;
    const index = driver.logs.findIndex(l => l.offline_id === offlineId);
    if (index >= 0) driver.logs.splice(index, 1);
  }

  public changeLogAttribute(
    driverId: number,
    offlineId: string,
    attribute: string,
    oldValue: any,
    newValue: any,
  ) {
    const log = this.getLog(driverId, offlineId, true);
    oldValue = normaliseAttr(oldValue);
    newValue = normaliseAttr(newValue);
    if (includes(LOG_ATTRIBUTES, attribute)) {
      if (!isAttrEqual(oldValue, newValue)) {
        log[attribute] = [oldValue, newValue];
      } else {
        delete log[attribute];
      }
    }
  }

  public changeEventAttribute(
    driverId: number,
    offlineId: string,
    attribute: string,
    oldValue: string | number,
    newValue: string | number,
    drivingPeriodId: number = null,
  ): void {
    let event: ILogSuggestionEventData;
    oldValue = normaliseAttr(oldValue);
    newValue = normaliseAttr(newValue);

    if (!isAttrEqual(oldValue, newValue)) {
      event = this.getEvent(driverId, offlineId, true);
      event[attribute] = [oldValue, newValue];
      if (drivingPeriodId) event.driving_period_id = drivingPeriodId;
    } else {
      event = this.getEvent(driverId, offlineId);
      if (event) delete event[attribute];
    }
  }

  public createEvent(driverId: number, eventData: ILogEvent, excludeOfflineId: boolean = false): string {
    const offlineId: string = !excludeOfflineId ? this.setEventOfflineId(eventData) : null;
    const event = this.getEvent(driverId, offlineId, true);
    for (let attr of Object.keys(eventData)) {
      let value = eventData[attr];
      attr = snakeCase(attr);
      if (!includes(EVENT_ATTRIBUTES, attr)) continue;
      value = normaliseAttr(value);
      if (!isNil(value)) event[attr] = [null, value];
    }
    if (eventData.sourceDrivingPeriodId) {
      event.source_driving_period_id = eventData.sourceDrivingPeriodId;
    }

    if (eventData.unassignDrivingPeriodId) {
      event.unassign_driving_period_id = eventData.unassignDrivingPeriodId;
    }
    return offlineId;
  }

  public removeEvent(driverId: number, eventData: ILogEvent, unassignDrivingPeriodId?: number): void {
    const event = this.getEvent(driverId, eventData.offlineId, true);
    for (let attr of Object.keys(eventData)) {
      let value = eventData[attr];
      attr = snakeCase(attr);
      if (!includes(EVENT_ATTRIBUTES, attr)) continue;
      value = normaliseAttr(value);
      if (!isNil(value)) event[attr] = [value, null];
    }
    if (unassignDrivingPeriodId) event.unassign_driving_period_id = unassignDrivingPeriodId;
  }

  // Sets event changes by comparing originalLog with newLog. Optionally uses intermediateLog to determine what has
  // changed in newLog and overrides existing changes
  public setEventChangesFromLogs(originalLog: Log, newLog: Log, intermediateLog: Log = null): void {
    const originalEvents = originalLog.events;
    const newEvents = newLog.events;
    const driverId = originalLog.driverId;
    const logStartTime = originalLog.startTime;
    const logEndTime = originalLog.endTime;

    // New/Changed events
    for (const event of newEvents) {
      const offlineId = event.offlineId;
      const originalEvent = originalEvents.find(e => e.offlineId === offlineId);
      if (originalEvent) {
        for (const attr of Object.keys(event)) {
          const formattedAttr = snakeCase(attr);
          if (!includes(EVENT_ATTRIBUTES, formattedAttr)) continue;
          this.changeEventAttribute(driverId, offlineId, formattedAttr, originalEvent[attr], event[attr]);
        }
      } else {
        this.createEvent(driverId, event);
      }
    }

    // Deleted events
    for (const event of originalEvents) {
      const offlineId = event.offlineId;
      const newEvent = newEvents.find(e => e.offlineId === offlineId);
      if (!newEvent && event.time.isBetween(logStartTime, logEndTime, null, '[)')) {
        this.removeEvent(driverId, event);
      }
    }

    // Remove event changes for events created in intermediateLog but removed from newLog
    const deletedEventOfflineIds: string[] = [];
    const eventChanges = this.getEventChangesForDriver(driverId);
    if (intermediateLog) {
      const originalEventOfflineIds: string[] = map(originalEvents, 'offlineId');
      const newEventOfflineIds: string[] = map(newEvents, 'offlineId');
      const intermediateEvents = intermediateLog.events;
      for (const event of intermediateEvents) {
        const offlineId = event.offlineId;
        const isNewEvent: boolean = includes(newEventOfflineIds, offlineId);
        const isOriginalEvent: boolean = includes(originalEventOfflineIds, offlineId);
        if (!isNewEvent && !isOriginalEvent && event.time.isBetween(logStartTime, logEndTime, null, '[)')) {
          deletedEventOfflineIds.push(offlineId);
        }
      }
    }
    for (const offlineId of deletedEventOfflineIds) {
      const index = eventChanges.findIndex(e => e.offline_id === offlineId);
      eventChanges.splice(index, 1);
    }

    // Check for special case where the only changes are addition of sentinel events and remove them
    // so empty log suggestions can be detected
    const firstEvent: ILogEvent = first(originalEvents);
    const lastEvent: ILogEvent = last(originalEvents);
    const hasEventChange = (eventChange: string[], newValue: string | number): boolean => {
      return eventChange && eventChange[0] === null && eventChange[1] === newValue;
    };
    const checkSentinelEvent = (eventChange: ILogSuggestionEventData, event: ILogEvent, time: IMoment) => {
      return hasEventChange(eventChange.time, toDateFormat(time)) &&
        hasEventChange(eventChange.type, event.type) &&
        (isNil(eventChange.location) || hasEventChange(eventChange.location, event.location)) &&
        (isNil(eventChange.notes) || hasEventChange(eventChange.notes, event.notes))
      ;
    };
    const isSentinelEventChange = (eventChange: ILogSuggestionEventData) => {
      return checkSentinelEvent(eventChange, firstEvent, logStartTime) || checkSentinelEvent(eventChange, lastEvent, logEndTime);
    };

    // Remove empty event changes
    clone(eventChanges).forEach((change: ILogSuggestionEventData, index: number) => {
      if (Object.keys(change).length <= 1) eventChanges.splice(index, 1);
    });
    if (eventChanges.length <= 2 && every(eventChanges, isSentinelEventChange)) {
      eventChanges.length = 0;
    }
  }

  public changeRemarkAttribute(
    driverId: number,
    offlineId: string,
    attribute: string,
    oldValue: string | number,
    newValue: string | number,
  ): void {
    let remark: ILogSuggestionRemarkData;
    oldValue = normaliseAttr(oldValue);
    newValue = normaliseAttr(newValue);

    if (!isAttrEqual(oldValue, newValue)) {
      remark = this.getRemark(driverId, offlineId, true);
      remark[attribute] = [oldValue, newValue];
    } else {
      remark = this.getRemark(driverId, offlineId);
      if (remark) delete remark[attribute];
    }
  }

  public createRemark(driverId: number, remarkData: ILogRemark): string {
    const offlineId = remarkData.offlineId || generateUuid();
    const remark = this.getRemark(driverId, offlineId, true);
    for (const attr of Object.keys(remarkData)) {
      let value = remarkData[attr];
      if (!includes(REMARK_ATTRIBUTES, attr)) continue;
      value = normaliseAttr(value);
      if (!isNil(value)) remark[attr] = [null, value];
    }
    return offlineId;
  }

  public removeRemark(driverId: number, remarkData: ILogRemark): void {
    const remark = this.getRemark(driverId, remarkData.offlineId, true);
    for (const attr of Object.keys(remarkData)) {
      let value = remarkData[attr];
      if (!includes(REMARK_ATTRIBUTES, attr)) continue;
      value = normaliseAttr(value);
      if (!isNil(value)) remark[attr] = [value, null];
    }
  }

  // Sets remark changes by comparing originalLog with newLog. Optionally uses intermediateLog to determine what has
  // changed in newLog and overrides existing changes
  public setRemarkChangesFromLogs(originalLog: Log, newLog: Log, intermediateLog: Log = null) {
    const originalRemarks = originalLog.remarks;
    const newRemarks = newLog.remarks;
    const driverId = originalLog.driverId;
    const logStartTime = originalLog.startTime;
    const logEndTime = originalLog.endTime;

    for (const remark of newRemarks) {
      const offlineId = remark.offlineId;
      const originalRemark = originalRemarks.find(r => r.offlineId === offlineId);
      if (originalRemark) {
        for (const attr of Object.keys(remark)) {
          if (!includes(REMARK_ATTRIBUTES, attr)) continue;
          this.changeRemarkAttribute(driverId, offlineId, attr, originalRemark[attr], remark[attr]);
        }
      } else {
        this.createRemark(driverId, remark);
      }
    }

    // Deleted remarks
    for (const remark of originalRemarks) {
      const offlineId = remark.offlineId;
      const newRemark = newRemarks.find(r => r.offlineId === offlineId);
      if (!newRemark && remark.time.isBetween(logStartTime, logEndTime, null, '[)')) {
        this.removeRemark(driverId, remark);
      }
    }

    // Remove remark changes for remarks created in intermediateLog but removed from newLog
    const deletedRemarkOfflineIds: string[] = [];
    const remarkChanges = this.getRemarkChangesForDriver(driverId);
    if (intermediateLog) {
      const originalRemarkOfflineIds: string[] = map(originalRemarks, 'offlineId');
      const newRemarkOfflineIds: string[] = map(newRemarks, 'offlineId');
      const intermediateRemarks = intermediateLog.remarks;
      for (const remark of intermediateRemarks) {
        const offlineId = remark.offlineId;
        const isNewRemark: boolean = includes(newRemarkOfflineIds, offlineId);
        const isOriginalRemark: boolean = includes(originalRemarkOfflineIds, offlineId);
        if (!isNewRemark && !isOriginalRemark && remark.time.isBetween(logStartTime, logEndTime, null, '[)')) {
          deletedRemarkOfflineIds.push(offlineId);
        }
      }
    }
    for (const offlineId of deletedRemarkOfflineIds) {
      const index = remarkChanges.findIndex(r => r.offline_id === offlineId);
      remarkChanges.splice(index, 1);
    }
  }

  public reassignDrivingPeriod(
    drivingPeriod: IDrivingPeriod,
    vehicle: IServerLogVehicle,
    hourlyBreadcrumbs: IHourlyBreadcrumb[],
    receivingLogs: Log[],
    originalLogs: Log[] = null,
    options: IReassignTripOptions = {
      setTotalPCMeters: false,
      isBulkOperation: false,
    },
  ) {
    const { setTotalPCMeters, isBulkOperation } = options;
    const startTime = drivingPeriod.startTime as IMoment;
    const endTime = drivingPeriod.endTime;
    const startKms = drivingPeriod.startKilometers;
    const endKms = drivingPeriod.endKilometers;
    const odometers = this.odometerEntriesForLogs(startKms, endKms, receivingLogs || originalLogs, hourlyBreadcrumbs);
    let newDrivingEvent;
    let originalEvent: ILogEvent;

    // Update original logs' events if driving period is being re-assigned
    if (originalLogs) {
      const firstOriginalLog: Log = first(originalLogs);
      const lastOriginalLog: Log = last(originalLogs);
      const originalDriverId = firstOriginalLog.driverId;
      if (isBulkOperation) {
        originalEvent = find(firstOriginalLog.events, e => e.offlineId === drivingPeriod.startEventOfflineId);
      } else {
        originalEvent = find(firstOriginalLog.events, e => e.drivingPeriod && e.drivingPeriod.id === drivingPeriod.id);
      }

      // Remove original driving period event
      this.removeEvent(originalDriverId, originalEvent, !receivingLogs ? drivingPeriod.id : null);

      // Merge events before and after driving period event if they are of the same type
      const originalStartEvent = firstOriginalLog.getEventAt(startTime.clone().subtract(1, 'second'));
      const originalEndEvent = lastOriginalLog.getEventStartingAt(endTime);
      if (originalStartEvent && originalEndEvent) {
        if (originalStartEvent.type === originalEndEvent.type) {
          this.removeEvent(originalDriverId, originalEndEvent, !receivingLogs ? drivingPeriod.id : null);
        } else {
          if (!receivingLogs) {
            // Create end event for the driving period, do not include offline_id
            this.createEvent(originalDriverId, {
              type: 'on_duty',
              time: originalEndEvent.time,
              location: originalEndEvent.location,
              notes: originalEndEvent.notes,
              unassignDrivingPeriodId: drivingPeriod.id,
            } as ILogEvent, true);
          }
        }
      }

      newDrivingEvent = pick(originalEvent, ['location', 'notes']);

      // Create day boundary events if driving period spans multiple days to avoid rendering issues
      if (originalLogs.length > 1 && originalStartEvent) {
        originalLogs.forEach(l => {
          const time = l.startTime;
          const eventData = {
            type: originalStartEvent.type,
            time,
          } as ILogEvent;
          this.createEvent(originalDriverId, eventData);
        });
      }

      // Remove odometer entry from original logs
      if (!vehicle.prevent_auto_odometer_entry) {
        odometers.forEach((odo, i) => {
          const log = originalLogs[i];
          if (log) this.removeOdometerEntryFromLog(log, odo, vehicle, setTotalPCMeters);
        });
      }

      // Add all affected original log ids to the suggestion
      originalLogs.forEach(l => this.addLogId(l.id));
    } else {
      newDrivingEvent = { location: drivingPeriod.startLocation };
    }

    if (receivingLogs) {
      const firstLog = first(receivingLogs);
      const lastLog = last(receivingLogs);
      const endEvent = lastLog.getEventAt(endTime);
      // Update receiving logs' events
      // Add new driving event with source_driving_period_id specified
      const receivingDriverId = firstLog.driverId;
      const newDrivingEventData = {
        type: 'driving',
        time: startTime,
        location: newDrivingEvent.location,
        notes: newDrivingEvent.notes,
        sourceDrivingPeriodId: drivingPeriod.id,
      } as ILogEvent;
      newDrivingEvent.offlineId = this.createEvent(receivingDriverId, newDrivingEventData);
      const newDrivingEndEventData = {
        type: endEvent.type,
        time: endTime,
        sourceDrivingPeriodId: drivingPeriod.id,
        location: drivingPeriod.endLocation,
      } as ILogEvent;
      this.createEvent(receivingDriverId, newDrivingEndEventData);

      // Remove any events between start and end of new driving period event
      for (const log of receivingLogs) {
        const eventsToDelete = log.getEventsBetween(startTime, endTime);
        eventsToDelete.forEach(e => this.removeEvent(receivingDriverId, e));
      }

      // Add odometer entry in receiving logs
      if (!vehicle.prevent_auto_odometer_entry) {
        odometers.forEach((odo, i) => {
          const log = receivingLogs[i];
          this.addOdometerEntryToLog(log, odo, vehicle, isBulkOperation, setTotalPCMeters);
        });
      }

      // Add all affected receiving log ids to the suggestion
      receivingLogs.forEach(l => this.addLogId(l.id));
    } else {
      newDrivingEvent.offlineId = this.setEventOfflineId(originalEvent);
    }

    this.addDrivingPeriodId(drivingPeriod.id);
    return newDrivingEvent;
  }

  public addLogId(id: number): void {
    id = Number(id);
    if (!includes(this.data.log_ids, id)) {
      this.data.log_ids.push(id);
    }
  }

  public addDrivingPeriodId(id: number): void {
    id = Number(id);
    if (!includes(this.data.driving_period_ids, id)) {
      this.data.driving_period_ids.push(id);
    }
  }

  // Check the log suggestion body and remove empty changes
  public clean(): void {
    const body: ILogSuggestionBodyObject = this.data.body;
    for (const driverId of Object.keys(body)) {
      const changes: ILogSuggestionBody = body[driverId];
      const logChanges: ILogSuggestionLogData[] = changes.logs;
      const eventChanges: ILogSuggestionEventData[] = changes.events;
      const remarkChanges: ILogSuggestionRemarkData[] = changes.remarks;

      if (logChanges) {
        const emptyLogChanges = [];
        logChanges.forEach((log, index) => {
          if (log.offline_id && Object.keys(log).length === 1) {
            emptyLogChanges.push(index);
          }
        });
        emptyLogChanges.forEach(i => logChanges.splice(i, 1));
      }
      if (eventChanges) {
        const emptyEventChanges = [];
        eventChanges.forEach((event, index) => {
          if (event.offline_id && Object.keys(event).length === 1) {
            emptyEventChanges.push(index);
          }
        });
        emptyEventChanges.forEach(i => eventChanges.splice(i, 1));
      }
      if (remarkChanges) {
        const emptyRemarkChanges = [];
        remarkChanges.forEach((remark, index) => {
          if (remark.offline_id && Object.keys(remark).length === 1) {
            emptyRemarkChanges.push(index);
          }
        });
        emptyRemarkChanges.forEach(i => remarkChanges.splice(i, 1));
      }
    }
  }

  public isEmpty(): boolean {
    const body: ILogSuggestionBodyObject = this.data.body;
    let hasChanges: boolean = false;
    for (const driverId of Object.keys(body)) {
      const changes: ILogSuggestionBody = body[driverId];
      if (changes.logs && changes.logs.length) {
        hasChanges = true;
        break;
      } else if (changes.events && changes.events.length) {
        hasChanges = true;
        break;
      } else if (changes.remarks && changes.remarks.length) {
        hasChanges = true;
        break;
      }
    }
    return !hasChanges;
  }

  public clone(): LogSuggestion {
    const proto: LogSuggestion = Object.getPrototypeOf(this);
    const logSuggestionClone: LogSuggestion = Object.create(proto);
    logSuggestionClone.data = cloneDeep(this.data);
    return logSuggestionClone;
  }

  public checkPCLimitOnLogs(
    drivingPeriod: any,
    hourlyBreadcrumbs: IHourlyBreadcrumb[],
    logs: Log[],
    canadaOnly: boolean,
    threshold: number,
  ): boolean {
    const odometers: IOdometerEntry[] = this.odometerEntriesForLogs(
      drivingPeriod.startKilometers,
      drivingPeriod.endKilometers,
      logs,
      hourlyBreadcrumbs,
    );
    let isLimitReached = false;

    for (let i = 0; i < odometers.length; i++) {
      const log: Log = logs[i];
      if (canadaOnly) {
        isLimitReached = logs[i].hasCanadaCycle && this.isPCLimitExceededOnLog(log, odometers[i], threshold);
      } else isLimitReached = this.isPCLimitExceededOnLog(log, odometers[i], threshold);

      if (isLimitReached) break;
    }

    return isLimitReached;
  }

  public setTotalPCMetersForLogs(
    drivingPeriod: any,
    logs: Log[],
    hourlyBreadcrumbs: IHourlyBreadcrumb[],
    operation: 'add' | 'remove',
  ): void {
    const odometers = this.odometerEntriesForLogs(
      drivingPeriod.startKilometers,
      drivingPeriod.endKilometers,
      logs,
      hourlyBreadcrumbs,
    );
    odometers.forEach((odo, i) => {
      const log = logs[i];
      if (log) {
        this.setTotalPCMeters(log, odo.start, odo.end, operation);
        this.addLogId(log.id);
      }
    });
  }

  public getUpdatedLogAttributeValue(log: Log, attribute: string) {
    const logChanges = this.getLog(log.driverId, log.offlineId, false);
    return logChanges && logChanges[attribute] ? logChanges[attribute][1] : clone(log.data[attribute]);
  }

  private isPCLimitExceededOnLog(log: Log, entry: IOdometerEntry, threshold: number): boolean {
    const tripDistanceMeters: number = (entry.end - entry.start) * METERS_PER_KM;
    const totalPCDistance: number = this.getUpdatedLogAttributeValue(log, 'total_pc_meters') || log.totalPCMeters;

    return totalPCDistance + tripDistanceMeters > threshold;
  }

  private setEventOfflineId(eventData: ILogEvent): string {
    return eventData.offlineId || generateUuid();
  }

  private odometerEntriesForLogs(
    start: number,
    end: number,
    logs: Log[],
    hourlyBreadcrumbs: IHourlyBreadcrumb[],
  ): IOdometerEntry[] {
    const odometers: IOdometerEntry[] = [];
    // Only proceed if both start and end odometer readings are present
    if (start != null && end != null) {
      logs.forEach((log, i) => {
        const time = log.endTime;
        const breadcrumb = hourlyBreadcrumbs.find(b => b.time.isSame(time));
        let endOdometer: number;
        if (breadcrumb) {
          endOdometer = breadcrumb.odometer;
        } else if (i === logs.length - 1) {
          endOdometer = end;
        } else {
          const numRemainingLogs = logs.length - i;
          const diff = end - start;
          endOdometer = start + (diff / numRemainingLogs);
        }
        odometers.push({
          start,
          end: endOdometer,
          metric_units: null,
        });
        start = endOdometer;
      });
    }
    return odometers;
  }

  private addOdometerEntryToLog(
    log: Log,
    entry: IOdometerEntry,
    vehicle: IServerLogVehicle,
    isBulkOperation: boolean = false,
    setTotalPCMeters: boolean = false,
  ) {
    const odometers = cloneDeep(this.getUpdatedLogAttributeValue(log, 'odometers')) || {};
    const vehicleNum = vehicle.number;
    const vehicleEntries = odometers[vehicleNum] = odometers[vehicleNum] || [];
    const metricUnits = vehicle.metric_units;
    const start = Math.floor(metricUnits ? entry.start : kmToMiles(entry.start));
    const end = Math.floor(metricUnits ? entry.end : kmToMiles(entry.end));

    if (start <= end) {
      vehicleEntries.push({
        start,
        end,
        metric_units: metricUnits,
      });
    }
    const totalMiles = totalMilesFromOdometers(odometers, log.metricUnits);
    this.changeLogAttribute(log.driverId, log.offlineId, 'odometers', log.odometers, odometers);
    if (setTotalPCMeters) this.setTotalPCMeters(log, entry.start, entry.end, 'add');
    this.changeLogAttribute(log.driverId, log.offlineId, 'total_miles', log.totalMiles, totalMiles);

    // Add vehicle to vehicle ids if it is not present already
    const originalVehicleIds = !isBulkOperation ? map(log.vehicles, 'id') : clone(log.data.vehicle_ids);
    const vehicleIds = this.getUpdatedLogAttributeValue(log, 'vehicle_ids') || clone(originalVehicleIds);
    const vehicleIndex = vehicleIds.indexOf(vehicle.id);
    if (vehicleIndex < 0) {
      vehicleIds.push(vehicle.id);
      this.changeLogAttribute(log.driverId, log.offlineId, 'vehicle_ids', originalVehicleIds, vehicleIds);

    }
  }

  private removeOdometerEntryFromLog(log: Log, entry: IOdometerEntry, vehicle: any, setTotalPCMeters: boolean = false) {
    const odometers = cloneDeep(this.getUpdatedLogAttributeValue(log, 'odometers')) || {};
    const vehicleNum = vehicle.number;
    const vehicleEntries = odometers[vehicleNum] = odometers[vehicleNum] || [];
    const metricUnits: boolean = vehicle.metric_units;
    const start = Math.floor(metricUnits ? entry.start : kmToMiles(entry.start));
    const end = Math.floor(metricUnits ? entry.end : kmToMiles(entry.end));
    const index = findIndex(vehicleEntries, e => e.start == start && e.end == end && e.metric_units === metricUnits);

    // Remove entry if an exact match is found
    if (index >= 0) {
      vehicleEntries.splice(index, 1);
      if (vehicleEntries.length === 0) delete odometers[vehicleNum]; // Remove vehicle if it has no other entries

      const totalMiles = totalMilesFromOdometers(odometers, log.metricUnits);
      if (setTotalPCMeters) this.setTotalPCMeters(log, entry.start, entry.end, 'remove');
      this.changeLogAttribute(log.driverId, log.offlineId, 'odometers', log.odometers, odometers);
      this.changeLogAttribute(log.driverId, log.offlineId, 'total_miles', log.totalMiles, totalMiles);
    }
  }

  private setTotalPCMeters(
    log: Log,
    start: number,
    end: number,
    operation: 'add' | 'remove',
    ): void {
    /**
     * get updated value for total_pc_meters using getUpdatedLogAttributeValue and recalculate
     * subtract/add the PC meters from the log based on the operation
     */
    const totalPCDistance: number = (
      this.getUpdatedLogAttributeValue(log, 'total_pc_meters') || log.totalPCMeters
    ) / METERS_PER_KM;

    const diff: number = end - start;
    const updatedPCKms: number = max([(operation === 'add' ? totalPCDistance + diff : totalPCDistance - diff), 0]);

    this.changeLogAttribute(
      log.driverId,
      log.offlineId,
      'total_pc_meters',
      log.totalPCMeters,
      Math.floor(updatedPCKms * METERS_PER_KM),
    );
  }
}
